import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { AuthProvider } from '../shared/auth/auth-context.js';
import { isApiError } from '../shared/api/errors.js';

/**
 * Application-wide providers.
 *
 * The query client's defaults encode how this product should behave rather than
 * relying on library defaults, because the difference matters for financial
 * data: never retry something the server explicitly rejected, and never leave a
 * user looking at figures that quietly went stale.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          // 4xx means the request was understood and refused. Retrying it just
          // burns rate limit and delays the error the user needs to see.
          if (isApiError(error) && error.httpStatus < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: {
        // A mutation may have partially succeeded; retrying could duplicate a
        // financial record. Retries are opt-in, per mutation, never global.
        retry: false,
      },
    },
  });
}

export interface AppProvidersProps {
  readonly queryClient: QueryClient;
  readonly children: ReactNode;
}

export function AppProviders({ queryClient, children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>{children}</AuthProvider>
    </QueryClientProvider>
  );
}
